import { matchRoutes } from 'react-router-dom';
import vivosolRouteConfig from '../routes/vivosolRouteConfig';
import { toSlug } from './strings';

function joinPaths(parentPath = '', childPath = '') {
  if (!childPath) {
    return parentPath || '/';
  }
  if (childPath.startsWith('/')) {
    return childPath;
  }
  return `${parentPath.replace(/\/$/, '')}/${childPath}` || '/';
}

export function resolveRoutePath(path, params = {}) {
  return Object.entries(params).reduce(
    (resolved, [key, value]) => resolved.replace(`:${key}`, encodeURIComponent(value ?? '')),
    path,
  );
}

function canShow(nav, hasPermission, metricFlags, roleId) {
  if (!nav || nav.visible === false) {
    return false;
  }
  if (nav.metricFlag && metricFlags && metricFlags[nav.metricFlag] === false) {
    return false;
  }
  if (Array.isArray(nav.appRoleIds) && nav.appRoleIds.length) {
    if (!nav.appRoleIds.includes(Number(roleId))) {
      return false;
    }
  }
  return !nav.permission || !hasPermission || hasPermission(nav.permission);
}

function plantChildren(plants = []) {
  return [
    ...plants.map((plant) => ({
      label: String(plant),
      path: `/portfolio/dashboard/${toSlug(String(plant))}`,
    })),
    { label: 'View All Sites', path: '/portfolio' },
  ];
}

/**
 * Builds a rendered navigation model from the same objects consumed by
 * React Router. Parent/child relationships in the route tree become sidebar
 * sections/submenus; hidden and unauthorized nodes are omitted.
 */
export function buildNavigation(
  placement,
  {
    params = {},
    plants = [],
    hasPermission,
    metricFlags,
    roleId,
    routes = vivosolRouteConfig,
  } = {},
) {
  const items = [];
  const actions = [];

  function visit(routeList, parentPath = '', parentItem = null) {
    routeList.forEach((route) => {
      const routePath = route.index ? parentPath : joinPaths(parentPath, route.path);
      const routeNav = route.handle?.nav;
      const belongsHere = routeNav?.placements?.includes(placement);
      let currentParent = parentItem;

      if (belongsHere && canShow(routeNav, hasPermission, metricFlags, roleId)) {
        const item = {
          label: routeNav.label,
          path: resolveRoutePath(routeNav.path ?? routePath, params),
          icon: routeNav.icon,
          area: routeNav.area,
          footerLabel: routeNav.footerLabel,
          order: routeNav.order ?? 0,
        };

        if (routeNav.dynamicChildren === 'plants') {
          item.submenu = plantChildren(plants);
        }

        if (parentItem) {
          parentItem.submenu = [...(parentItem.submenu ?? []), item];
        } else {
          items.push(item);
        }
        currentParent = item;
      }

      if (route.handle?.sidebarActionsPlacement === placement) {
        route.handle.sidebarActions?.forEach((action) => {
          if (!action.permission || !hasPermission || hasPermission(action.permission)) {
            actions.push({ ...action });
          }
        });
      }

      if (route.children) {
        visit(route.children, routePath, currentParent);
      }
    });
  }

  visit(routes);

  return [...items, ...actions]
    .sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
    .map((item) => ({
      ...item,
      submenu: item.submenu
        ?.sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
        .map(({ order, ...child }) => child),
    }));
}

export function getNavigationMatches(pathname) {
  return matchRoutes(vivosolRouteConfig, pathname) ?? [];
}

export function getSidebarPlacement(pathname) {
  return getNavigationMatches(pathname)
    .map((match) => match.route.handle?.sidebarPlacement)
    .filter(Boolean)
    .at(-1);
}
